'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Progress } from '@/components/ui/progress'
import { ArrowLeft, ArrowRight, Upload, Plus, Trash2, Image as ImageIcon, Loader2, Sparkles } from 'lucide-react'
import Image from 'next/image'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { DatePicker } from '@/components/date-picker'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useClassify } from '@/hooks/useClassify'
import { useTaxEstimate } from '@/hooks/useTaxEstimate'
import { useParseInvoice } from '@/hooks/useParseInvoice'
import { uploadInvoiceAsset, uploadProductImage } from '@/lib/storage/images'
import type {
  Customer,
  CreateConsignmentRequest,
  HelmCommunicationChannel,
  HelmInquiryStage,
  HelmServiceType,
  TaxEstimateResponse,
} from '@/lib/api/types'
import { apiClient } from '@/lib/api/client'

const serviceTypeOptions: HelmServiceType[] = [
  'PRODUCT_SOURCING',
  'SHIPPING_ONLY',
  'PVOC_TESTING',
  'CUSTOMS_CLEARANCE',
  'END_TO_END_IMPORT',
]

const inquiryStageOptions: HelmInquiryStage[] = [
  'NEW_INQUIRY',
  'QUALIFYING',
  'QUOTED',
  'AWAITING_CUSTOMER',
  'ACTIVE',
  'BLOCKED',
  'COMPLETED',
  'CLOSED',
]

const communicationChannelOptions: HelmCommunicationChannel[] = ['EMAIL', 'WHATSAPP', 'BOTH', 'NONE']

function formatLabel(value: string) {
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

type ConsignmentData = {
  customerId: string
  mode: string
  serviceType: HelmServiceType
  inquiryStage: HelmInquiryStage
  preferredCommunicationChannel: HelmCommunicationChannel
  nextAction: string
  serviceNotes: string
  destinationPort: string
  expectedArrival: string
  trackingNumber: string
  insurance: string
  invoiceFile?: File
  items: Array<{
    id: string
    name: string
    quantity: number
    unitPrice: number
    description: string
    hsCode?: string
    hsCodeDescription?: string
    aiConfidence?: number
    image?: File
    imageUrl?: string
    isClassifying?: boolean
  }>
  cifValue: number
}

export default function NewConsignmentPage() {
  const router = useRouter()
  const [step, setStep] = useState(1)
  const [isDragging, setIsDragging] = useState(false)
  const [taxEstimateData, setTaxEstimateData] = useState<TaxEstimateResponse | null>(null)
  const [customers, setCustomers] = useState<Customer[]>([])
  const [customersLoading, setCustomersLoading] = useState(true)
  const [uploadingImage, setUploadingImage] = useState<string | null>(null)
  const [parsingInvoice, setParsingInvoice] = useState(false)
  const [consignmentData, setConsignmentData] = useState<ConsignmentData>({
    customerId: '',
    mode: '',
    serviceType: 'END_TO_END_IMPORT',
    inquiryStage: 'NEW_INQUIRY',
    preferredCommunicationChannel: 'EMAIL',
    nextAction: '',
    serviceNotes: '',
    destinationPort: 'Kampala',
    expectedArrival: '',
    trackingNumber: '',
    insurance: 'no',
    items: [],
    cifValue: 0,
  })

  const { classify } = useClassify()
  const { estimate, loading: taxLoading, error: taxError } = useTaxEstimate()
  const { parse } = useParseInvoice()

  const totalSteps = 5
  const progress = (step / totalSteps) * 100

  useEffect(() => {
    let cancelled = false

    async function loadCustomers() {
      try {
        const customerList = await apiClient.listCustomers()
        if (!cancelled) {
          setCustomers(customerList)
        }
      } catch (error) {
        console.error('Failed to load customers:', error)
      } finally {
        if (!cancelled) {
          setCustomersLoading(false)
        }
      }
    }

    loadCustomers()

    return () => {
      cancelled = true
    }
  }, [])

  const handleNext = () => {
    if (step < totalSteps) setStep(step + 1)
  }

  const handleBack = () => {
    if (step > 1) setStep(step - 1)
  }

  const handleInputChange = (field: keyof ConsignmentData, value: unknown) => {
    setConsignmentData((prev) => ({ ...prev, [field]: value }))
  }

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = e.dataTransfer.files
    if (files && files[0]) {
      // Check if file is PDF or image
      const file = files[0]
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'image/gif']
      if (validTypes.includes(file.type)) {
        handleInvoiceUpload(file)
      }
    }
  }

  // Handle invoice file upload and parsing
  const handleInvoiceUpload = async (file: File) => {
    handleInputChange('invoiceFile', file)
    setParsingInvoice(true)

    try {
      const publicUrl = await uploadInvoiceAsset(file)

      // Parse invoice using OCR
      const parsedData = await parse(publicUrl)

      if (parsedData && parsedData.items.length > 0) {
        // Convert parsed items to consignment items
        const newItems = parsedData.items.map((item) => ({
          id: Math.random().toString(36).substr(2, 9),
          name: item.name,
          quantity: item.quantity,
          unitPrice: item.unit_price,
          description: item.description || '',
        }))

        setConsignmentData((prev) => ({
          ...prev,
          items: [...prev.items, ...newItems],
        }))
      }
    } catch (error) {
      console.error('Invoice parsing failed:', error)
      // Continue anyway - user can manually add items
    } finally {
      setParsingInvoice(false)
    }
  }

  // Image upload handler for product images
  const handleImageUpload = async (itemId: string, file: File) => {
    setUploadingImage(itemId)
    try {
      const imageUrl = await uploadProductImage(file)
      updateItem(itemId, 'imageUrl', imageUrl)
      updateItem(itemId, 'image', file)
    } catch (error) {
      console.error('Image upload failed:', error)
      alert(error instanceof Error ? error.message : 'Failed to upload image')
    } finally {
      setUploadingImage(null)
    }
  }

  // Classify item using AI
  const handleClassifyItem = async (itemId: string) => {
    const item = consignmentData.items.find(i => i.id === itemId)
    if (!item?.description && !item?.imageUrl) {
      alert('Please provide a description or image for classification')
      return
    }

    updateItem(itemId, 'isClassifying', true)

    try {
      const result = await classify({
        description: item.description,
        image_url: item.imageUrl,
      })

      if (result) {
        updateItem(itemId, 'hsCode', result.hs_code)
        updateItem(itemId, 'aiConfidence', result.confidence)
        updateItem(itemId, 'hsCodeDescription', result.description)
      }
    } catch (error) {
      console.error('Classification failed:', error)
    } finally {
      updateItem(itemId, 'isClassifying', false)
    }
  }

  // Calculate tax estimate
  const handleCalculateTax = async () => {
    const totalValue = consignmentData.items.reduce(
      (sum, item) => sum + item.quantity * item.unitPrice,
      0
    )

    if (totalValue === 0 || consignmentData.items.length === 0) {
      alert('Please add items before calculating tax')
      return
    }

    // Use first item's HS code or default
    const hsCode = consignmentData.items[0]?.hsCode || '0000.00.00'

    const result = await estimate({
      hs_code: hsCode,
      invoice_value: totalValue,
      freight: 0,
      insurance: consignmentData.insurance === 'yes' ? totalValue * 0.02 : 0,
    })

    if (result) {
      setTaxEstimateData(result)
    }
  }

  const addItem = () => {
    const newItem = {
      id: Math.random().toString(36).substr(2, 9),
      name: '',
      quantity: 1,
      unitPrice: 0,
      description: '',
    }
    setConsignmentData((prev) => ({
      ...prev,
      items: [...prev.items, newItem],
    }))
  }

  const removeItem = (id: string) => {
    setConsignmentData((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== id),
    }))
  }

  const updateItem = (id: string, field: string, value: unknown) => {
    setConsignmentData((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      ),
    }))
  }

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (isSubmitting) return
    setIsSubmitting(true)

    try {
      // Validate required fields
      if (!consignmentData.mode || !['air', 'sea', 'truck'].includes(consignmentData.mode)) {
        throw new Error('Please select a shipping mode (air, sea, or truck)')
      }
      if (!consignmentData.customerId) {
        throw new Error('Please select a customer')
      }
      if (!consignmentData.items.length) {
        throw new Error('Please add at least one item')
      }

      // Map frontend data to API format
      const requestData: CreateConsignmentRequest = {
        customer_id: consignmentData.customerId,
        mode: consignmentData.mode as 'air' | 'sea' | 'truck',
        service_type: consignmentData.serviceType,
        inquiry_stage: consignmentData.inquiryStage,
        preferred_communication_channel: consignmentData.preferredCommunicationChannel,
        next_action: consignmentData.nextAction.trim() || undefined,
        service_notes: consignmentData.serviceNotes.trim() || undefined,
        destination_port: consignmentData.destinationPort,
        expected_arrival: consignmentData.expectedArrival || undefined,
        tracking_number: consignmentData.trackingNumber || undefined,
        insurance: consignmentData.insurance === 'yes',
        items: consignmentData.items.map(item => ({
          name: item.name,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          hs_code: item.hsCode,
          hs_code_description: item.hsCodeDescription,
          ai_confidence: item.aiConfidence,
          image_url: item.imageUrl,
        })),
        tax_estimate: taxEstimateData ? {
          hs_code: taxEstimateData.hs_code,
          cif_value: taxEstimateData.input_cif,
          currency: taxEstimateData.input_currency || 'USD',
          target_currency: taxEstimateData.target_currency || 'UGX',
          exchange_rate: taxEstimateData.exchange_rate,
          import_duty: taxEstimateData.import_duty,
          vat: taxEstimateData.vat,
          withholding_tax: taxEstimateData.withholding_tax,
          infra_levy: taxEstimateData.infra_levy,
          excise_duty: taxEstimateData.excise_duty,
          total_tax: taxEstimateData.total_tax,
          breakdown: taxEstimateData.breakdown,
        } : undefined,
      }

      // Call backend API
      await apiClient.createConsignment(requestData)

      // Success!
      router.push('/consignments')
    } catch (error) {
      console.error('Submission failed:', error)
      alert(error instanceof Error ? error.message : 'Failed to submit consignment. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button
          onClick={() => router.back()}
          variant="ghost"
          size="sm"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">New Consignment</h1>
          <p className="text-muted-foreground mt-1">Step {step} of {totalSteps}</p>
        </div>
      </div>

      <Progress value={progress} className="h-2" />

      {/* Step 1: Shipping Details */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Shipping Details</CardTitle>
            <CardDescription>Enter your shipment information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="customer">Customer</Label>
              <Select
                value={consignmentData.customerId}
                onValueChange={(value) => handleInputChange('customerId', value)}
                disabled={customersLoading || customers.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder={customersLoading ? 'Loading customers...' : 'Select customer'} />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.company_name || customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!customersLoading && customers.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Create a customer before opening a consignment.
                </p>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="serviceType">Service Type</Label>
                <Select
                  value={consignmentData.serviceType}
                  onValueChange={(value) => handleInputChange('serviceType', value as HelmServiceType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceTypeOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {formatLabel(option)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="inquiryStage">Inquiry Stage</Label>
                <Select
                  value={consignmentData.inquiryStage}
                  onValueChange={(value) => handleInputChange('inquiryStage', value as HelmInquiryStage)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {inquiryStageOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {formatLabel(option)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="preferredCommunicationChannel">Preferred Channel</Label>
                <Select
                  value={consignmentData.preferredCommunicationChannel}
                  onValueChange={(value) => handleInputChange('preferredCommunicationChannel', value as HelmCommunicationChannel)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {communicationChannelOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {formatLabel(option)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="nextAction">Next Action</Label>
                <Input
                  id="nextAction"
                  value={consignmentData.nextAction}
                  onChange={(e) => handleInputChange('nextAction', e.target.value)}
                  placeholder="Quote, chase invoice, confirm PVoC..."
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="serviceNotes">Service Notes</Label>
              <Textarea
                id="serviceNotes"
                value={consignmentData.serviceNotes}
                onChange={(e) => handleInputChange('serviceNotes', e.target.value)}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mode">Mode of Shipping</Label>
              <Select
                value={consignmentData.mode}
                onValueChange={(value) => handleInputChange('mode', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select shipping mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="air">Air</SelectItem>
                  <SelectItem value="sea">Sea</SelectItem>
                  <SelectItem value="truck">Truck</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="destinationPort">Destination Port</Label>
              <Input
                id="destinationPort"
                value={consignmentData.destinationPort}
                onChange={(e) => handleInputChange('destinationPort', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expectedArrival">Expected Date of Arrival</Label>
              <DatePicker
                value={consignmentData.expectedArrival}
                onChange={(date) => handleInputChange('expectedArrival', date)}
                placeholder="Select arrival date"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="trackingNumber">Tracking Number (Optional)</Label>
              <Input
                id="trackingNumber"
                value={consignmentData.trackingNumber}
                onChange={(e) => handleInputChange('trackingNumber', e.target.value)}
                placeholder="e.g., TRK-2024-001"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="insurance">Insurance</Label>
              <Select
                value={consignmentData.insurance}
                onValueChange={(value) => handleInputChange('insurance', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Invoice Upload */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Invoice Upload</CardTitle>
            <CardDescription>
              Upload your invoice or skip to manually declare items
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div 
              className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors cursor-pointer ${
                isDragging 
                  ? 'border-primary bg-primary/10' 
                  : 'hover:bg-accent/50'
              }`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="font-medium mb-2">Drop invoice here or click to upload</p>
              <p className="text-sm text-muted-foreground mb-4">
                {parsingInvoice ? 'Parsing invoice with Tarifix...' : 'Supports PDF and images'}
              </p>
              <input
                type="file"
                accept=".pdf,image/*"
                className="hidden"
                id="invoice-upload"
                disabled={parsingInvoice}
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleInvoiceUpload(e.target.files[0])
                  }
                }}
              />
              <label htmlFor="invoice-upload">
                <Button variant="outline" asChild disabled={parsingInvoice}>
                  <span>
                    {parsingInvoice ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Parsing...
                      </>
                    ) : (
                      'Choose File'
                    )}
                  </span>
                </Button>
              </label>
            </div>

            {consignmentData.invoiceFile && (
              <div className="bg-muted rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-primary/10 rounded flex items-center justify-center">
                    <Upload className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{consignmentData.invoiceFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(consignmentData.invoiceFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleInputChange('invoiceFile', undefined)}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}

            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                Don&apos;t have an invoice?{' '}
                <button
                  onClick={handleNext}
                  className="text-primary hover:underline font-medium"
                >
                  Skip and manually declare items
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Item Declaration */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Item Declaration</CardTitle>
                <CardDescription>
                  {consignmentData.invoiceFile
                    ? 'Review and edit parsed items from invoice'
                    : 'Manually declare your items'}
                </CardDescription>
              </div>
              <Button
                onClick={addItem}
                size="sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {consignmentData.items.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed rounded-lg">
                <p className="text-muted-foreground mb-4">No items added yet</p>
                <Button onClick={addItem} variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Item
                </Button>
              </div>
            ) : (
              consignmentData.items.map((item, index) => (
                <div key={item.id} className="bg-muted rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline">
                      Item {index + 1}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeItem(item.id)}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Item Name</Label>
                      <Input
                        value={item.name}
                        onChange={(e) => updateItem(item.id, 'name', e.target.value)}
                        placeholder="e.g., Laptop"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Quantity</Label>
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value))}
                        min="1"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Unit Price (USD)</Label>
                      <Input
                        type="number"
                        value={item.unitPrice}
                        onChange={(e) => updateItem(item.id, 'unitPrice', parseFloat(e.target.value))}
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Total (USD)</Label>
                      <Input
                        value={`$${(item.quantity * item.unitPrice).toFixed(2)}`}
                        disabled
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Description (for HS Code classification)</Label>
                    <Textarea
                      value={item.description}
                      onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                      placeholder="Detailed description to help with classification..."
                      rows={2}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <ImageIcon className="h-4 w-4" />
                      Product Image (Optional)
                    </Label>
                    {item.imageUrl ? (
                      <div className="flex items-center gap-2">
                        <div className="relative h-20 w-20">
                          <Image
                            src={item.imageUrl}
                            alt={item.name}
                            fill
                            className="object-cover rounded border"
                            sizes="80px"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            updateItem(item.id, 'imageUrl', undefined)
                            updateItem(item.id, 'image', undefined)
                          }}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/*"
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          disabled={uploadingImage === item.id}
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handleImageUpload(item.id, e.target.files[0])
                            }
                          }}
                        />
                        {uploadingImage === item.id && (
                          <div className="absolute inset-0 flex items-center justify-center bg-background/80">
                            <Loader2 className="h-4 w-4 animate-spin" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 4: HS Code Classification */}
      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle>HS Code Classification</CardTitle>
            <CardDescription>
              Review and confirm AI-suggested HS codes for your items
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {consignmentData.items.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No items to classify</p>
            ) : (
              consignmentData.items.map((item, index) => (
                <div key={item.id} className="bg-muted rounded-lg p-4">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-medium">{item.name || `Item ${index + 1}`}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                      {item.imageUrl && (
                        <div className="relative mt-2 h-16 w-16">
                          <Image
                            src={item.imageUrl}
                            alt={item.name}
                            fill
                            className="object-cover rounded border"
                            sizes="64px"
                          />
                        </div>
                      )}
                    </div>
                    {item.aiConfidence && (
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Sparkles className="h-3 w-3" />
                        AI {(item.aiConfidence * 100).toFixed(0)}%
                      </Badge>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <div className="flex-1 space-y-2">
                        <Label>HS Code</Label>
                        <Input
                          value={item.hsCode || ''}
                          onChange={(e) => updateItem(item.id, 'hsCode', e.target.value)}
                          className="font-mono"
                          placeholder="0000.00.00"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          type="button"
                          onClick={() => handleClassifyItem(item.id)}
                          disabled={item.isClassifying || (!item.description && !item.imageUrl)}
                          className="flex items-center gap-2"
                        >
                          {item.isClassifying ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              AI Classifying...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4" />
                              Classify
                            </>
                          )}
                        </Button>
                      </div>
                    </div>

                    {item.hsCodeDescription && (
                      <p className="text-xs text-muted-foreground">
                        {item.hsCodeDescription}
                      </p>
                    )}

                    {!item.description && !item.imageUrl && (
                      <Alert>
                        <AlertDescription className="text-xs">
                          Add a description or image in Step 3 to use AI classification
                        </AlertDescription>
                      </Alert>
                    )}                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 5: Review & Submit */}
      {step === 5 && (
        <Card>
          <CardHeader>
            <CardTitle>Review & Tax Estimation</CardTitle>
            <CardDescription>Review your consignment details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted rounded-lg p-4 space-y-3">
              <h3 className="font-semibold">Workflow</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Service</p>
                  <p>{formatLabel(consignmentData.serviceType)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Stage</p>
                  <p>{formatLabel(consignmentData.inquiryStage)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Channel</p>
                  <p>{formatLabel(consignmentData.preferredCommunicationChannel)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Next Action</p>
                  <p>{consignmentData.nextAction || 'Not set'}</p>
                </div>
              </div>
              {consignmentData.serviceNotes && (
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">{consignmentData.serviceNotes}</p>
              )}
            </div>

            <div className="bg-muted rounded-lg p-4 space-y-3">
              <h3 className="font-semibold">Shipping Details</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Mode</p>
                  <p className="capitalize">{consignmentData.mode}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Destination</p>
                  <p>{consignmentData.destinationPort}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Expected Arrival</p>
                  <p>{consignmentData.expectedArrival}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Tracking Number</p>
                  <p>{consignmentData.trackingNumber || 'N/A'}</p>
                </div>
              </div>
            </div>

            <div className="bg-muted rounded-lg p-4 space-y-3">
              <h3 className="font-semibold">Items ({consignmentData.items.length})</h3>
              {consignmentData.items.map((item) => (
                <div key={item.id} className="flex justify-between items-start pb-2 border-b last:border-0">
                  <div>
                    <p>{item.name}</p>
                    <p className="text-sm text-muted-foreground">
                      Qty: {item.quantity} × ${item.unitPrice} = ${(item.quantity * item.unitPrice).toFixed(2)}
                    </p>
                  </div>
                  <Badge variant="outline" className="font-mono text-xs">
                    {item.hsCode || '8471.30.00'}
                  </Badge>
                </div>
              ))}
            </div>

            <div className="border rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Tax Estimation</h3>
                {!taxEstimateData && (
                  <Button
                    onClick={handleCalculateTax}
                    disabled={taxLoading || consignmentData.items.length === 0}
                  >
                    {taxLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Calculating...
                      </>
                    ) : (
                      'Calculate Tax'
                    )}
                  </Button>
                )}
              </div>

              {taxError && (
                <Alert className="mb-4">
                  <AlertDescription>{taxError}</AlertDescription>
                </Alert>
              )}

              {taxEstimateData ? (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Exchange Rate</span>
                    <span>1 {taxEstimateData.input_currency} = {taxEstimateData.exchange_rate.toFixed(2)} {taxEstimateData.target_currency}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>CIF Value</span>
                    <span>{taxEstimateData.target_currency} {taxEstimateData.cif_ugx.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Import Duty</span>
                    <span>{taxEstimateData.target_currency} {taxEstimateData.import_duty.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>VAT</span>
                    <span>{taxEstimateData.target_currency} {taxEstimateData.vat.toLocaleString()}</span>
                  </div>
                  {taxEstimateData.withholding_tax > 0 && (
                    <div className="flex justify-between">
                      <span>Withholding Tax</span>
                      <span>{taxEstimateData.target_currency} {taxEstimateData.withholding_tax.toLocaleString()}</span>
                    </div>
                  )}
                  {taxEstimateData.excise_duty > 0 && (
                    <div className="flex justify-between">
                      <span>Excise Duty</span>
                      <span>{taxEstimateData.target_currency} {taxEstimateData.excise_duty.toLocaleString()}</span>
                    </div>
                  )}
                  {taxEstimateData.infra_levy > 0 && (
                    <div className="flex justify-between">
                      <span>Infrastructure Levy</span>
                      <span>{taxEstimateData.target_currency} {taxEstimateData.infra_levy.toLocaleString()}</span>
                    </div>
                  )}
                  <Separator className="my-2" />
                  <div className="flex justify-between font-bold text-lg">
                    <span>Total Tax</span>
                    <span>{taxEstimateData.target_currency} {taxEstimateData.total_tax.toLocaleString()}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCalculateTax}
                    className="w-full mt-4"
                  >
                    Recalculate
                  </Button>
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">Click calculate to get tax estimation</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation Buttons */}
      <Card>
        <CardContent className="flex items-center justify-between pt-6">
          <Button
            onClick={handleBack}
            variant="outline"
            disabled={step === 1}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          {step < totalSteps ? (
            <Button onClick={handleNext}>
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                'Submit Consignment'
              )}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
